import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { ContentCustom, PageHeaderCustom } from '../../../components';
import { OwnerShowCards } from './detailsCards/OwnerShowCards';
import { ReviewDrawer } from '../../../components/Review/ReviewDrawer';
import { deleteOwner } from './utils/deleteOwner';
import { MailingModal } from '../../../components/Mailing/MailingModal';
import { useSocketContext } from '../../../contexts/SocketContext';
import { useOwnerContext } from '../../../contexts/OwnerContext/OwnerContext';
import { ExtraPageHeader } from './ExtraPageHeader';

/**
 * The `ShowOwner` component displays detailed information about a specific owner.
 * The component fetches the owner data based on the ID retrieved from the URL parameters
 * and processes dwelling and animal photos before displaying the details in nested components.
 * It also allows users to edit or delete the owner information with respective buttons and actions.
 *
 * It uses several hooks and contexts:
 * - `useParams` to get the ID parameter from the URL.
 * - `useTranslation` to facilitate the translation of texts.
 * - `useSocketContext` to access the socket context.
 * - `useOwnerContext` to access the owner context.
 * - `useEffect` to manage fetching the owner data and other side effects.
 *
 * @component
 *
 * @example
 * <ShowOwner />
 *
 * @returns {React.Element} A React component that displays the detailed information about an owner and allows editing or deleting the owner information.
 */
export const ShowOwner = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const { t } = useTranslation();
  const { profileLock } = useSocketContext();
  const { questionnaireType, setQuestionnaireType, ownerSupervisions } =
    useOwnerContext();

  const [reviewDrawerOpen, setReviewDrawerOpen] = useState(false);
  const [supervisionRating, setSupervisionRating] = useState({});
  const [showReviewDrawer, setShowReviewDrawer] = useState(false);
  const [refreshData, setRefreshData] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [templateType, setTemplateType] = useState('SEND_MAIL');
  const [documentType, setDocumentType] = useState(null);
  const [reviewData, setReviewData] = useState({});
  const [editReviewDrawer, setEditReviewDrawer] = useState(false);

  const { owner, getOwner, getOwnerSupervisions, isLoading } =
    useOwnerContext();

  useEffect(() => {
    (async () => {
      await Promise.all([getOwner(id), getOwnerSupervisions(id)]);
    })();
  }, []);

  const getOwnerTitle = () => {
    if (!Object.keys(owner).length) return null;
    const civilityKey = owner?.civility ? `owners.tags.${owner.civility}` : '';
    const civilityText = civilityKey ? t(civilityKey) : '';
    return `${civilityText} ${owner?.last_name} ${owner?.first_name} n°${owner?.reference}`;
  };

  return (
    <>
      <PageHeaderCustom
        title={getOwnerTitle()}
        extra={
          <ExtraPageHeader
            setIsEmailModalOpen={setIsEmailModalOpen}
            setTemplateType={setTemplateType}
            setDocumentType={setDocumentType}
            profileLock={profileLock}
            id={id}
            deleteOwner={deleteOwner}
          />
        }
      />
      <ContentCustom transparent>
        <OwnerShowCards
          isLoading={isLoading}
          setReviewDrawerOpen={setReviewDrawerOpen}
          form={form}
          setSupervisionRating={setSupervisionRating}
          setShowReviewDrawer={setShowReviewDrawer}
          refreshData={refreshData}
          setRefreshData={setRefreshData}
          setReviewData={setReviewData}
          reviewData={reviewData}
          setEditReviewDrawer={setEditReviewDrawer}
        />
      </ContentCustom>

      <ReviewDrawer
        setOpen={setReviewDrawerOpen}
        open={reviewDrawerOpen}
        customInstanceForm={form}
        supervision={supervisionRating}
        showReviewDrawer={showReviewDrawer}
        setSupervision={setSupervisionRating}
        setForceRefresh={setRefreshData}
        forceRefresh={refreshData}
        reviewData={reviewData}
        editReviewDrawer={editReviewDrawer}
        supervisions={ownerSupervisions}
        questionnaireType={questionnaireType}
        setQuestionnaireType={setQuestionnaireType}
      />

      {isEmailModalOpen && (
        <MailingModal
          isModalOpen={isEmailModalOpen}
          setIsModalOpen={setIsEmailModalOpen}
          recipients={[{ ...owner, customer_type: 'OWNER' }]}
          recipient={owner?.user?._id}
          recordID={id}
          filterID={null}
          customFormInstance={form}
          templateType={templateType}
          documentType={documentType}
          collection="owners"
        />
      )}
    </>
  );
};
